import { Box, Button, SxProps, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { formatPrice } from "src/Utils/number";
import {
  colorCTAOrange,
  colorCTAOrangeHover,
  colorCTAOrangeLight,
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  radiusCard,
} from "src/design/tokens";
import { SubscriptionTier } from "src/data/landing/queries/subscriptionTiers";
import { tierPerkKeys } from "src/business/subscription/tierPerks";
import { TierPerkList } from "src/components/LandingPage/components/TierPerkList";

interface SubscriptionTierCardProps {
  tier: SubscriptionTier;
  selected: boolean;
  onSelect: () => void;
  sx?: SxProps;
}

export const SubscriptionTierCard = ({
  tier,
  selected,
  onSelect,
  sx,
}: SubscriptionTierCardProps) => {
  const { t, i18n } = useTranslation();

  const borderColor = selected || tier.popular ? colorCTAOrange : colorBorder;

  return (
    <Box
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        cursor: "pointer",
        p: 3,
        pt: tier.popular ? 3.5 : 3,
        borderRadius: radiusCard,
        border: `${selected || tier.popular ? 2 : 1}px solid ${borderColor}`,
        backgroundColor: selected ? colorCTAOrangeLight : colorSurface,
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        "&:hover": { borderColor: colorCTAOrange },
        ...sx,
      }}
    >
      {tier.popular && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translate(-50%, -50%)",
            px: 2,
            py: 0.5,
            borderRadius: 999,
            backgroundColor: colorCTAOrange,
            color: colorSurface,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {t("landingPage.subscriptionTiers.popular")}
        </Box>
      )}

      <Typography
        sx={{
          color: colorCTAOrange,
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        {t(`landingPage.subscriptionTiers.tiers.${tier.name}.title`)}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: 40,
            lineHeight: 1,
            color: colorTextPrimary,
          }}
        >
          {formatPrice(tier.monthlyPrice, i18n.language)}
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: 14, color: colorTextMuted }}
        >
          {t("landingPage.subscriptionTiers.priceSuffix")}
        </Typography>
      </Box>

      {/* The discounted rate, not the yearly total: it is the number that
          compares with the monthly price printed above it. */}
      <Typography sx={{ color: colorTextMuted, fontSize: 13, mt: 0.75 }}>
        {t("landingPage.subscriptionTiers.annualRate", {
          amount: formatPrice(tier.annualMonthlyPrice, i18n.language),
        })}
      </Typography>

      <Typography sx={{ color: colorTextMuted, fontSize: 14, mt: 1.5, mb: 2 }}>
        {t(`landingPage.subscriptionTiers.tiers.${tier.name}.description`)}
      </Typography>

      <TierPerkList perkKeys={tierPerkKeys(tier)} sx={{ mb: 3, flexGrow: 1 }} />

      <Button
        fullWidth
        variant={selected ? "contained" : "outlined"}
        disableElevation
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        startIcon={
          selected ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
          ) : undefined
        }
        sx={{
          mt: "auto",
          textTransform: "none",
          fontWeight: 600,
          fontSize: 15,
          py: 1.2,
          borderRadius: radiusCard,
          ...(selected
            ? {
                backgroundColor: colorCTAOrange,
                color: colorSurface,
                "&:hover": { backgroundColor: colorCTAOrangeHover },
              }
            : {
                color: colorCTAOrange,
                borderColor: colorCTAOrange,
                "&:hover": {
                  borderColor: colorCTAOrangeHover,
                  backgroundColor: colorCTAOrangeLight,
                },
              }),
        }}
      >
        {selected
          ? t("landingPage.subscriptionTiers.selectedCta")
          : t("landingPage.subscriptionTiers.selectCta")}
      </Button>
    </Box>
  );
};
